import { useRef, useState, useEffect, useCallback } from "react";

/**
 * useAutoSave — Google-Docs-style debounced auto-save hook.
 *
 * Mirrors the pattern used in WorkerShiftDetail for shift notes so all
 * long-form document editors behave consistently.
 *
 * Usage:
 *   const { queueAuto, flushAll, saveStatus, globalSave } = useAutoSave();
 *
 *   // In a field's onChange:
 *   queueAuto("fieldKey", () => api.updateDocument(id, { values }));
 *
 *   // globalSave: { state: "idle"|"pending"|"saving"|"saved"|"error", at: Date|null }
 */
export function useAutoSave({ debounceMs = 1500 } = {}) {
  const [saveStatus, setSaveStatus] = useState({}); // key → { state, at }
  const autoTimers = useRef({});
  const pendingRef = useRef({}); // key → () => Promise

  // Flush a single pending key immediately.
  const flushKey = useCallback(async (key) => {
    const fn = pendingRef.current[key];
    if (!fn) return;
    delete pendingRef.current[key];
    clearTimeout(autoTimers.current[key]);
    setSaveStatus((st) => ({ ...st, [key]: { state: "saving" } }));
    try {
      await fn();
      setSaveStatus((st) => ({ ...st, [key]: { state: "saved", at: new Date() } }));
    } catch {
      setSaveStatus((st) => ({ ...st, [key]: { state: "error", at: new Date() } }));
    }
  }, []);

  // Schedule a debounced save for `key`.
  const queueAuto = useCallback(
    (key, fn) => {
      pendingRef.current[key] = fn;
      setSaveStatus((st) => ({ ...st, [key]: { state: "pending" } }));
      clearTimeout(autoTimers.current[key]);
      autoTimers.current[key] = setTimeout(() => flushKey(key), debounceMs);
    },
    [flushKey, debounceMs]
  );

  // Flush all pending keys at once (used on navigation / page hide).
  const flushAll = useCallback(() => {
    Object.keys(pendingRef.current).forEach((k) => flushKey(k));
  }, [flushKey]);

  // Cancel a single key without saving.
  const cancelKey = useCallback((key) => {
    delete pendingRef.current[key];
    clearTimeout(autoTimers.current[key]);
  }, []);

  // Cancel everything (used when the editor is closed / document changes).
  const cancelAll = useCallback(() => {
    pendingRef.current = {};
    Object.values(autoTimers.current).forEach(clearTimeout);
    autoTimers.current = {};
  }, []);

  // Flush on page hide / tab switch so the user never loses work.
  useEffect(() => {
    const onHide = () => flushAll();
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
    return () => {
      window.removeEventListener("pagehide", onHide);
      Object.values(autoTimers.current).forEach(clearTimeout);
    };
  }, [flushAll]);

  // beforeunload guard — browser will show "Leave site? Changes may not be saved."
  useEffect(() => {
    const handler = (e) => {
      if (Object.keys(pendingRef.current).length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // Aggregate all per-key states into a single top-level status object
  // (same logic as WorkerShiftDetail's `globalSave` derived value).
  const vals = Object.values(saveStatus);
  let globalSave = null;
  if (vals.length > 0) {
    if (vals.some((v) => v.state === "pending" || v.state === "saving")) {
      globalSave = { state: "saving" };
    } else if (vals.some((v) => v.state === "error")) {
      globalSave = { state: "error" };
    } else {
      const last = vals.filter((v) => v.at).sort((a, b) => b.at - a.at)[0];
      if (last) globalSave = { state: "saved", at: last.at };
    }
  }

  // True when there are pending saves — used for in-app nav guards.
  const hasPending = Object.keys(pendingRef.current).length > 0;

  return {
    queueAuto,
    flushAll,
    flushKey,
    cancelKey,
    cancelAll,
    saveStatus,
    globalSave,
    hasPending,
    /** Reset all save-status keys (call when opening a new document). */
    resetStatus: () => setSaveStatus({}),
  };
}
